import { createFileRoute, Link } from '@tanstack/react-router';
import { trpc } from '@org/clients/school';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  MultipleSelector,
  Skeleton,
} from '@org/ui';
import type { Option } from '@org/ui';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeacherId } from '../../components/teacher-picker';
import { EventFilters } from '../../components/event-filters';

export const Route = createFileRoute('/classes/')({
  component: ClassesPage,
  validateSearch: (search: Record<string, unknown>) => ({
    teacherId: typeof search.teacherId === 'string' ? search.teacherId : undefined,
  }),
});

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function extractFirstUrl(raw?: string | null) {
  if (!raw) return null;
  const match = raw.match(/https?:\/\/[^\s)]+/i);
  if (match?.[0]) return match[0];
  const meetMatch = raw.match(/(?:^|\s)(meet\.google\.com\/[^\s)]+)/i);
  if (meetMatch?.[1]) return `https://${meetMatch[1]}`;
  return null;
}

function formatParticipantLabel(participants: string[]) {
  if (participants.length <= 2) {
    return participants.join(', ');
  }
  const [first, second, ...rest] = participants;
  return `${first}, ${second} +${rest.length} more`;
}

function EventClassSelector({
  eventId,
  initialClasses,
  teacherId,
}: {
  eventId: string;
  initialClasses: Array<{ classId: string; className: string | null; classDescription: string | null }>;
  teacherId?: string;
}) {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const attachMutation = trpc.teacher.events.attachClasses.useMutation({
    onSuccess: () => {
      utils.teacher.events.next.invalidate();
      utils.teacher.events.past.invalidate();
    },
  });

  const initialOptions: Option[] = initialClasses.map(c => ({
    value: c.classId,
    label: c.className ?? 'Unnamed',
  }));

  const [selected, setSelected] = useState<Option[]>(initialOptions);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback((newSelected: Option[]) => {
    setSelected(newSelected);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      attachMutation.mutate({
        teacherId,
        eventId,
        classIds: newSelected.map(o => o.value),
      });
    }, 1000);
  }, [eventId, teacherId, attachMutation]);

  const handleSearch = useCallback(async (query: string): Promise<Option[]> => {
    if (query.length < 3) return [];
    const results = await utils.teacher.events.searchClasses.fetch({
      query,
      teacherId,
    });
    return results.map(c => ({
      value: c.classId,
      label: c.name ?? 'Unnamed',
    }));
  }, [teacherId, utils]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
        {t('teacher.classSelector.label')}
      </p>
      <MultipleSelector
        value={selected}
        onChange={handleChange}
        onSearch={handleSearch}
        delay={300}
        placeholder={t('teacher.classSelector.placeholder')}
        emptyIndicator={
          <p className="text-center text-sm text-gray-500">{t('teacher.classSelector.empty')}</p>
        }
        loadingIndicator={
          <p className="text-center text-sm text-gray-400">{t('common.status.searching')}</p>
        }
        commandProps={{ shouldFilter: false }}
      />
      {attachMutation.isPending && (
        <p className="text-xs text-gray-400">{t('common.status.saving')}</p>
      )}
    </div>
  );
}

function ClassesPage() {
  const teacherId = useTeacherId();
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.teacher.events.past.useInfiniteQuery(
    { teacherId },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    },
  );
  const events = data?.pages.flatMap((p) => p.events) ?? [];

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);
  const { t } = useTranslation('admin');
  const participantHeading = t('teacher.nextClassesPage.participantsHeading');
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [groupId, setGroupId] = useState<string>('all');
  const [studentSearch, setStudentSearch] = useState<string>('');

  const groupOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const event of events) {
      for (const group of event.groups ?? []) {
        if (!seen.has(group.groupId)) {
          seen.set(group.groupId, group.label);
        }
      }
    }
    return Array.from(seen, ([gId, label]) => ({ groupId: gId, label }));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const search = studentSearch.toLowerCase().trim();
    return events.filter((event) => {
      if (showPendingOnly && event.scorecardId) return false;
      if (groupId !== 'all' && !event.groups?.some((g) => g.groupId === groupId)) return false;
      if (search && !event.participants?.some((p) => p.toLowerCase().includes(search))) return false;
      return true;
    });
  }, [events, showPendingOnly, groupId, studentSearch]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#cf2e2e]">
            {t('teacher.classesPage.eyebrow')}
          </p>
          <h1 className="text-3xl font-semibold text-gray-900">{t('teacher.classesPage.title')}</h1>
          <p className="text-sm text-gray-600">{t('teacher.classesPage.description')}</p>
        </div>
        <Button
          type="button"
          variant={showPendingOnly ? 'default' : 'outline'}
          onClick={() => setShowPendingOnly((prev) => !prev)}
          className="rounded-full"
        >
          {t('teacher.classesPage.showPendingOnly')}
        </Button>
        <Badge variant="outline" className="rounded-full border-rose-200 bg-white px-4 py-1 text-[#cf2e2e]">
          {t('teacher.classesPage.badgeLabel', { count: filteredEvents.length })}
        </Badge>
      </div>

      {!isLoading && groupOptions.length > 0 && (
        <EventFilters
          groupId={groupId}
          onGroupChange={setGroupId}
          groups={groupOptions}
          studentSearch={studentSearch}
          onStudentSearchChange={setStudentSearch}
        />
      )}

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} className="border border-rose-50 bg-white">
              <CardHeader className="space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-10 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && events.length === 0 && (
        <Card className="border border-dashed border-rose-200 bg-white/80">
          <CardHeader>
            <CardTitle>{t('teacher.classesPage.emptyTitle')}</CardTitle>
            <CardDescription>{t('teacher.classesPage.emptyDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && filteredEvents.length === 0 && events.length > 0 && showPendingOnly && (
        <Card className="border border-dashed border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle>{t('teacher.classesPage.allFilledTitle')}</CardTitle>
            <CardDescription>{t('teacher.classesPage.allFilledDescription')}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {filteredEvents.map((event) => {
          const statusLabel = event.scorecardId
            ? t('teacher.classesPage.statusFilled')
            : t('teacher.classesPage.statusPending');
          const statusColor = event.scorecardId ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
          const destinationScorecardId = event.scorecardId ?? 'new';
          const statusBadgeClass = `${statusColor} rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em]`;
          const locationUrl = extractFirstUrl(event.location);

          return (
            <Card
              key={event.eventId}
              className="border border-rose-50 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <CardHeader className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className={statusBadgeClass}>{statusLabel}</span>
                      <span className="text-xs uppercase tracking-[0.3em] text-gray-400">
                        {event.eventKind?.replace('-', ' ') ?? t('common.fallbacks.event')}
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-gray-900">
                        {event.title ?? t('common.fallbacks.untitledClass')}
                      </CardTitle>
                      <CardDescription className="text-sm text-gray-600">
                        {event.startTime
                          ? dateFormatter.format(new Date(event.startTime))
                          : t('common.fallbacks.noDate')}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-700">
                  {event.notesPreview ?? t('teacher.classesPage.notesFallback')}
                </p>
                {event.groups?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {event.groups.map((group) => (
                      <Badge
                        key={`${event.eventId}-${group.groupId}`}
                        className="rounded-full border border-rose-100 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#cf2e2e]"
                      >
                        {group.label}
                      </Badge>
                    ))}
                  </div>
                ) : null}
                {['private-class', 'tutoring'].includes(event.eventKind ?? '') && event.participants?.length ? (
                  <div className="rounded-2xl border border-dashed border-rose-100 bg-white/80 p-4 text-sm text-gray-600">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-400">{participantHeading}</p>
                    <p className="font-semibold text-gray-900">{formatParticipantLabel(event.participants)}</p>
                  </div>
                ) : null}
                <EventClassSelector
                  eventId={event.eventId}
                  initialClasses={event.classes ?? []}
                  teacherId={teacherId}
                />
                <div className="flex flex-wrap items-center gap-3">
                  <Button asChild className="rounded-full">
                    <Link
                      to="/event/$eventId/scorecard/$scorecardId"
                      params={{ eventId: event.eventId, scorecardId: destinationScorecardId }}
                    >
                      {event.scorecardId
                        ? t('teacher.classesPage.updateScorecard')
                        : t('teacher.classesPage.fillScorecard')}
                    </Link>
                  </Button>
                  <Button variant="ghost" asChild className="text-sm text-[#cf2e2e]">
                    <Link to="/homework">{t('teacher.classesPage.reviewHomework')}</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div ref={sentinelRef} className="h-4" />
      {isFetchingNextPage && (
        <div className="flex justify-center py-6">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-[#cf2e2e]" />
        </div>
      )}
    </div>
  );
}









